import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import api from '../services/api';
import publicApi from '../services/publicApi';
import { useAuth } from '../contexts/AuthContext';
import StarRating from './StarRating';
import { formatNumber, formatCurrency } from '../utils/format';
import { useTranslation } from 'react-i18next';

// Helper function to check if content should be hidden
function shouldHideContent(content) {
  return !content || content === 'Information not available' || content.trim() === '';
}

// Helper function to format large numbers with abbreviations
function formatLargeNumber(num) {
  if (!num || isNaN(num)) return 'N/A';
  
  const absNum = Math.abs(num);
  if (absNum >= 1e12) {
    return `${(num / 1e12).toFixed(1)}T`;
  } else if (absNum >= 1e9) {
    return `${(num / 1e9).toFixed(1)}B`;
  } else if (absNum >= 1e6) {
    return `${(num / 1e6).toFixed(1)}M`;
  } else if (absNum >= 1e3) {
    return `${(num / 1e3).toFixed(1)}K`;
  } else {
    return num.toLocaleString();
  }
}

function CoinDataTab({ symbol }) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const isLoggedIn = !!currentUser;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fetchingRef = useRef(false);

  useEffect(() => {
    console.log(`[CoinDataTab] useEffect triggered for symbol: ${symbol}, isLoggedIn: ${isLoggedIn}`);
    fetchCoinData();
  }, [symbol, isLoggedIn]);

    const fetchCoinData = async () => {
    if (fetchingRef.current) {
      console.log(`[CoinDataTab] Fetch already in progress for ${symbol}, skipping`);
      return;
    }
    
    fetchingRef.current = true;
    console.log(`[CoinDataTab] Starting fetch for ${symbol}, isLoggedIn: ${isLoggedIn}`);
    setLoading(true);
    setError(null);

    try {
      // Use public API for both logged and non-logged users for now
      // as it has the most reliable implementation for coins_data table access
      const apiEndpoint = `/api/public/coins/${symbol}/data`;
      
      console.log(`[CoinDataTab] Calling endpoint: ${apiEndpoint} (isLoggedIn: ${isLoggedIn})`);
      
      const response = await publicApi.get(`/api/public/coins/${symbol}/data`);
      
      console.log(`[CoinDataTab] API Response for ${symbol}:`, {
        status: response.status,
        exists: response.data?.exists,
        hasData: !!response.data?.data,
        hasNumerical: !!response.data?.numerical,
        numericalKeys: response.data?.numerical ? Object.keys(response.data.numerical) : [],
        numericalSample: response.data?.numerical ? {
          market_cap: response.data.numerical.market_cap,
          volume_24h: response.data.numerical.volume_24h,
          market_cap_rank: response.data.numerical.market_cap_rank
        } : null,
        numericalRaw: response.data?.numerical,
        numericalStringified: JSON.stringify(response.data?.numerical),
        fullResponse: response.data
      });
      
      console.log(`[CoinDataTab] Setting data state for ${symbol}:`, response.data);
      setData(response.data);
    } catch (err) {
      console.error(`[CoinDataTab] Error fetching coin data for ${symbol}:`, {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        fullError: err
      });
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
      console.log(`[CoinDataTab] Fetch completed for ${symbol}`);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || !data.exists) {
    return null; // Hide the tab when no data is available
  }

  const { data: coinData, numerical, last_refresh, hasAnalysisData, hasMarketData } = data;
  
  // Debug logging for render
  console.log(`[CoinDataTab] Rendering ${symbol} with data:`, {
    exists: data.exists,
    hasNumerical: !!numerical,
    numericalKeys: numerical ? Object.keys(numerical) : [],
    numericalDataExists: !!numerical,
    numericalLength: numerical ? Object.keys(numerical).length : 0,
    willShowMarketData: numerical && Object.keys(numerical).length > 0,
    numericalSample: numerical ? {
      market_cap: numerical.market_cap,
      market_cap_rank: numerical.market_cap_rank,
      volume_24h: numerical.volume_24h,
      price_change_percentage_24h: numerical.price_change_percentage_24h
    } : null,
    timestamp: new Date().toISOString()
  });

  return (
    <Box>
      {/* AI Analysis Section - Only show if analysis data is available */}
      {hasAnalysisData && coinData && (
        <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Project Overview
              </Typography>
              
              {coinData.category && !shouldHideContent(coinData.category) && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Category</Typography>
                  </Box>
                  <Chip 
                    label={coinData.category} 
                    color="primary" 
                    size="small" 
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              )}
              
              {coinData.utility && !shouldHideContent(coinData.utility) && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Utility</Typography>
                  </Box>
                  <Typography variant="body1">{coinData.utility}</Typography>
                </Box>
              )}
              
              {coinData.token_distribution && !shouldHideContent(coinData.token_distribution) && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Token Distribution</Typography>
                  </Box>
                  <Typography variant="body1">{coinData.token_distribution}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ecosystem
              </Typography>
              
              {coinData.investors && !shouldHideContent(coinData.investors) && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Key Investors</Typography>
                  </Box>
                  <Typography variant="body1">{coinData.investors}</Typography>
                </Box>
              )}
              
              {coinData.customers && !shouldHideContent(coinData.customers) && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Customers</Typography>
                  </Box>
                  <Typography variant="body1">{coinData.customers}</Typography>
                </Box>
              )}
              
              {coinData.partners && !shouldHideContent(coinData.partners) && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Partners</Typography>
                  </Box>
                  <Typography variant="body1">{coinData.partners}</Typography>
                </Box>
              )}
              
              {coinData.competitors && !shouldHideContent(coinData.competitors) && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Competitors</Typography>
                  </Box>
                  <Typography variant="body1">{coinData.competitors}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Full Width Section */}
        {coinData.other_important_data && !shouldHideContent(coinData.other_important_data) && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Additional Information
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {coinData.other_important_data}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      )}

      {/* Overall Score at the bottom - Only show if analysis data is available */}
      {hasAnalysisData && coinData && coinData.general_rating && (
        <Card sx={{ mt: 3, bgcolor: 'background.paper', boxShadow: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', letterSpacing: 1, color: 'primary.main' }}>
                {t('coin.analysis.overallScore', { defaultValue: 'Overall Score' })}
              </Typography>
              <StarRating 
                rating={coinData.general_rating} 
                size="large" 
                showLabel={false}
              />
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1 }}>
                {typeof coinData.general_rating === 'number' ? coinData.general_rating.toFixed(1) : coinData.general_rating || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                (out of 5)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default CoinDataTab; 